import React, { useState } from 'react';
import Navbar from '../../components/navbar/navbar';
import Footer from '../../components/footer/index';
import Diskon from '../../components/layanan/network_device/paket';
import { useDarkMode } from '../../components/darkmode/DarkModeContext'; // Import dark mode context
import { Wifi, Server, Cpu, Settings, Zap, Shield, CheckCircle, Users, Clock, MessageCircle, ArrowRight, Network } from 'lucide-react';

const NetworkDevicePage = () => {
  // Get dark mode state from context instead of local state
  const { isDarkMode } = useDarkMode();
  const [activeTab, setActiveTab] = useState('installation');

  const services = {
    installation: [
      {
        icon: <Wifi className="w-8 h-8" />,
        title: "Access Point Installation",
        description: "Pemasangan dan setup access point untuk coverage optimal",
        benefits: ["Site survey", "Optimal placement", "Channel optimization"]
      },
      {
        icon: <Server className="w-8 h-8" />,
        title: "Router & Switch Setup",
        description: "Instalasi dan konfigurasi router dan switch jaringan",
        benefits: ["VLAN configuration", "QoS setup", "Security policies"]
      },
      {
        icon: <Network className="w-8 h-8" />,
        title: "Mesh Network Deployment",
        description: "Implementasi jaringan mesh untuk coverage tanpa dead spot",
        benefits: ["Seamless roaming", "Scalable solution", "Central management"]
      }
    ],
    configuration: [
      {
        icon: <Settings className="w-8 h-8" />,
        title: "Network Optimization",
        description: "Tuning dan optimasi performa jaringan untuk kinerja terbaik",
        benefits: ["Bandwidth management", "Traffic shaping", "Performance monitoring"]
      },
      {
        icon: <Shield className="w-8 h-8" />,
        title: "Security Configuration",
        description: "Konfigurasi keamanan jaringan dan firewall protection",
        benefits: ["Firewall rules", "VPN setup", "Access control"]
      },
      {
        icon: <Users className="w-8 h-8" />,
        title: "Guest Network Setup",
        description: "Pembuatan jaringan tamu yang aman dan terisolasi",
        benefits: ["Captive portal", "Bandwidth limiting", "Time-based access"]
      }
    ]
  };

  const features = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: "High Performance",
      description: "Optimasi jaringan untuk kecepatan dan latency terbaik",
      color: "bg-blue-500"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Enterprise Security",
      description: "Keamanan tingkat enterprise dengan multi-layer protection",
      color: "bg-green-500"
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: "Professional Configuration",
      description: "Konfigurasi oleh ahli jaringan berpengalaman",
      color: "bg-purple-500"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Multi-User Support",
      description: "Dukungan untuk ratusan device connected simultaneously",
      color: "bg-yellow-500"
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "24/7 Monitoring",
      description: "Monitoring jaringan terus menerus untuk menjaga stabilitas",
      color: "bg-red-500"
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: "Quality Guarantee",
      description: "Garansi kualitas pekerjaan dan performa jaringan",
      color: "bg-indigo-500"
    }
  ];

  const brands = [
    { 
      name: "Ruijie", 
      logo: "/image/ruijie-logo.png", 
      description: "Enterprise networking solutions dengan performa tinggi",
      features: ["Cloud management", "High density", "AI optimization"]
    },
    { 
      name: "MikroTik", 
      logo: "/image/mikrotik-logo.png", 
      description: "RouterOS untuk fleksibilitas dan kontrol penuh",
      features: ["RouterOS", "Cost effective", "Advanced routing"]
    },
    { 
      name: "Ubiquiti", 
      logo: "/image/ubiquiti-logo.png", 
      description: "UniFi ecosystem untuk jaringan terintegrasi",
      features: ["UniFi controller", "Seamless roaming", "Beautiful design"]
    },
    { 
      name: "TP-Link", 
      logo: "/image/tplink-logo.png", 
      description: "Omada series untuk business networking",
      features: ["Central management", "Scalable", "Easy setup"]
    },
    { 
      name: "Cisco", 
      logo: "/image/cisco-logo.png", 
      description: "Enterprise-grade networking equipment",
      features: ["Enterprise features", "High reliability", "Advanced security"]
    },
    { 
      name: "Huawei", 
      logo: "/image/huawei-logo.png", 
      description: "CloudEngine series untuk jaringan cloud",
      features: ["Cloud management", "AI features", "High performance"]
    }
  ];

  const useCases = [
    {
      title: "Enterprise Office",
      description: "Jaringan korporat dengan multiple VLAN dan security policies",
      image: "/image/layanan/enterprise-office.png",
      benefits: ["Multi-VLAN setup", "Security policies", "Guest network"]
    },
    {
      title: "Education Campus",
      description: "Jaringan untuk kampus dengan coverage area luas dan multi-user",
      image: "/image/layanan/education-campus.png",
      benefits: ["Large coverage", "User management", "Content filtering"]
    },
    {
      title: "Hospitality Network",
      description: "Jaringan untuk hotel dan hospitality dengan captive portal",
      image: "/image/layanan/hospitality-network.png",
      benefits: ["Captive portal", "Bandwidth management", "Multi-SSID"]
    },
    {
      title: "Retail Business",
      description: "Jaringan untuk retail dengan POS integration dan security",
      image: "/image/layanan/retail-business.png",
      benefits: ["POS integration", "Customer WiFi", "Inventory management"]
    }
  ];


  const testimonials = [
    {
      name: "Budi Santoso",
      role: "IT Manager",
      company: "PT. Sentral Plaza",
      rating: 5,
      comment: "Instalasi perangkat Ruijie untuk gedung kami sangat profesional. Jaringan sekarang stabil dan cepat.",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=60&fit=crop&crop=face"
    },
    {
      name: "Diana Sari",
      role: "Network Administrator",
      company: "Universitas Teknologi",
      rating: 5,
      comment: "Konfigurasi MikroTik untuk kampus sangat membantu management jaringan yang kompleks.",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=60&h=60&fit=crop&crop=face"
    },
    {
      name: "Ahmad Rizki",
      role: "Hotel Manager",
      company: "Grand Hotel",
      rating: 5,
      comment: "Ubiquiti UniFi system untuk hotel kami memberikan experience terbaik untuk tamu dengan WiFi yang stabil.",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=60&h=60&fit=crop&crop=face"
    }
  ];

  // Fungsi untuk mengarahkan ke WhatsApp dengan pesan otomatis
  const contactViaWhatsApp = (packageName = "Network Device Installation") => {
    const phoneNumber = "6287778882022";
    const message = `Halo, saya tertarik dengan layanan ${packageName}. Bisakah saya mendapatkan informasi lebih lanjut?`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900' : 'bg-[#dfe4e0ff]'
    }`}>
      
      {/* Navigation */}
      <Navbar />
      
      {/* Hero Section */}
      <section
        className={`py-20 px-4 relative overflow-hidden ${
          isDarkMode ? 'bg-gray-900' : ''
        }`}
        style={{
          backgroundColor: isDarkMode ? '#111827' : '#dfe4e0ff',
          backgroundImage: isDarkMode
            ? "linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('/image/network-device-dark.jpg')"
            : "linear-gradient(rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8)), url('/image/network-device-hero.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className={`inline-flex items-center gap-2 ${
              isDarkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-[#1E9B4C]'
            } px-4 py-2 rounded-full text-sm font-medium mb-6`}>
              <Settings className="w-4 h-4" />
              Network Device Solutions
            </div>

            <h1
              className={`text-4xl md:text-6xl font-bold mb-6 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              Instalasi & Konfigurasi
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#1E9B4C] to-green-700">
                Perangkat Jaringan
              </span>
            </h1>

            <p
              className={`text-xl max-w-3xl mx-auto mb-8 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              Kami menyediakan layanan instalasi dan konfigurasi perangkat jaringan seperti 
              perangkat produk Ruijie, MikroTik, Ubiquiti, dan berbagai access point lainnya 
              untuk mengoptimalkan performa jaringan nirkabel Anda.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => contactViaWhatsApp("Network Device Installation")}
                className="bg-[#1E9B4C] hover:bg-green-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-5 h-5" />
                Konsultasi Gratis
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="absolute inset-0">
        <img
          src="https://telnet.net.id/assets/bg.png"
          alt="Background pattern"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/40 to-transparent dark:via-black/40"></div>
      </div>

      {/* Features Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2
              className={`text-3xl font-bold mb-4 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              Keunggulan Layanan Kami
            </h2>
            <p
              className={`text-lg ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              Solusi jaringan profesional untuk performa optimal dan keandalan maksimal
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#1E9B4C] to-green-600 mx-auto rounded-full mt-4"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`
                  group p-6 rounded-xl shadow-lg overflow-hidden 
                  transform hover:scale-105 hover:shadow-2xl transition-all duration-300
                  ${isDarkMode 
                    ? 'bg-gray-800 border border-gray-700 hover:border-[#1E9B4C] shadow-gray-900/50' 
                    : 'bg-white border border-gray-100 hover:border-[#1E9B4C] shadow-gray-300/20'
                  }
                `}
              >
                <div
                  className={`w-16 h-16 rounded-lg flex items-center justify-center text-white mb-4
                    ${feature.color || 'bg-[#1E9B4C]'}
                  `}
                >
                  {feature.icon}
                </div>

                <h3
                  className={`text-xl font-bold mb-3 transition-colors ${
                    isDarkMode
                      ? 'text-white group-hover:text-[#1E9B4C]'
                      : 'text-gray-900 group-hover:text-[#1E9B4C]'
                  }`}
                >
                  {feature.title}
                </h3>

                <p
                  className={`transition-colors ${
                    isDarkMode 
                      ? 'text-gray-300 group-hover:text-gray-200' 
                      : 'text-gray-600 group-hover:text-gray-700'
                  }`}
                >
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2
              className={`text-3xl font-bold mb-4 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              Layanan Kami
            </h2>
            <p
              className={`text-lg ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              Layanan lengkap untuk semua kebutuhan jaringan Anda
            </p>
          </div>

          <div className="flex justify-center mb-8">
            <div
              className={`inline-flex rounded-lg p-1 shadow-lg ${
                isDarkMode 
                  ? 'bg-gray-800 border border-gray-700' 
                  : 'bg-white border border-gray-200'
              }`}
            >
              {Object.keys(services).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                    activeTab === tab
                      ? 'bg-[#1E9B4C] text-white shadow-lg'
                      : isDarkMode
                      ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {tab === 'installation' ? 'Instalasi' : 'Konfigurasi'}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services[activeTab].map((service, index) => (
              <div
                key={index}
                className={`p-6 rounded-xl border-2 transition-all duration-300 hover:border-[#1E9B4C] hover:shadow-lg transform hover:scale-105 ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700 shadow-gray-900/50 hover:shadow-xl hover:bg-gray-750' 
                    : 'bg-white border border-gray-200 shadow-gray-300/20 hover:shadow-xl hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-[#1E9B4C] flex items-center justify-center text-white">
                    {service.icon}
                  </div>
                  <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {service.title}
                  </h3>
                </div>
                
                <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {service.description}
                </p>
                
                <div className="space-y-2">
                  {service.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#1E9B4C] flex-shrink-0" />
                      <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {benefit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2
              className={`text-3xl font-bold mb-4 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              Brand yang Kami Dukung
            </h2>
            <p
              className={`text-lg ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              Partner teknologi terkemuka untuk solusi jaringan terbaik
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {brands.map((brand, index) => (
              <div
                key={index}
                className={`p-6 rounded-xl border-2 transition-all duration-300 hover:border-[#1E9B4C] hover:shadow-lg transform hover:scale-105 ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700 shadow-gray-900/50 hover:shadow-xl hover:bg-gray-750' 
                    : 'bg-white border border-gray-200 shadow-gray-300/20 hover:shadow-xl hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src={brand.logo}
                    alt={brand.name}
                    className="h-12 object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                  />
                  <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {brand.name}
                  </h3>
                </div>
                
                <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {brand.description}
                </p>
                
                <div className="space-y-2">
                  {brand.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#1E9B4C] flex-shrink-0" />
                      <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className={`py-16 px-4 transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
      }`}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2
              className={`text-3xl font-bold mb-4 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              Berbagai Aplikasi Penggunaan
            </h2>
            <p
              className={`text-lg ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              Solusi jaringan untuk berbagai skala dan kebutuhan bisnis
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <div
                key={index}
                className={`rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 ${
                  isDarkMode 
                    ? 'bg-gray-800 border border-gray-700 shadow-gray-900/50' 
                    : 'bg-white border border-gray-100 shadow-gray-300/20'
                }`}
              >
                <div className="relative">
                  <img
                    src={useCase.image}
                    alt={useCase.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className={`absolute inset-0 ${
                    isDarkMode 
                      ? 'bg-gradient-to-t from-gray-800/20 to-transparent' 
                      : 'bg-gradient-to-t from-white/20 to-transparent'
                  }`}></div>
                </div>
                <div className="p-6">
                  <h3
                    className={`text-xl font-bold mb-3 ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {useCase.title}
                  </h3>
                  <p
                    className={`mb-4 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}
                  >
                    {useCase.description}
                  </p>
                  <div className="space-y-2">
                    {useCase.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-[#1E9B4C] flex-shrink-0" />
                        <span className={`text-sm ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          {benefit}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Diskon />

      {/* CTA Section */}
      <section
        className={`py-16 px-4 transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-800' : 'bg-[#1E9B4C]'
        }`}
      >
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Siap Optimalkan Jaringan Anda?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Dapatkan konsultasi gratis dari ahli jaringan kami untuk solusi terbaik 
            untuk kebutuhan bisnis Anda.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => contactViaWhatsApp("Network Device Installation")}
              className="bg-white text-[#1E9B4C] hover:bg-gray-100 font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center"
            >
              <MessageCircle className="w-5 h-5" />
              Konsultasi Gratis
            </button>
            <button 
              onClick={() => contactViaWhatsApp("Enterprise Network Solution")}
              className="border-2 border-white text-white hover:bg-white hover:text-[#1E9B4C] font-semibold px-8 py-4 rounded-lg transition-colors duration-200 flex items-center gap-2 justify-center"
            >
              <ArrowRight className="w-5 h-5" />
              Hubungi Sales
            </button>
          </div>
        </div>
      </section>
        
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default NetworkDevicePage;